// src/components/admin/Payments.jsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import './Payments.css';

const STATUS_LABELS = { unpaid: 'Unpaid', reported: 'Reported', paid: 'Paid' };
const STATUS_CLASSES = { unpaid: 'bp-pill-unpaid', reported: 'bp-pill-reported', paid: 'bp-pill-paid' };

const Payments = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reporting, setReporting] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const res = await fetch('https://demo-site.storii.com.pk/demo-api/admin/get-my-billing.php');
      const json = await res.json();
      if (json.success) setData(json);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (!data?.subscription?.payment_link) {
      toast.info('No payment link has been set yet.');
      return;
    }
    navigator.clipboard.writeText(data.subscription.payment_link);
    toast.success('Payment link copied');
  };

  const handleMarkPaid = async () => {
    setReporting(true);
    try {
      const res = await fetch('https://demo-site.storii.com.pk/demo-api/admin/report-payment.php', { method: 'POST' });
      const json = await res.json();
      if (json.success) {
        toast.success('Marked as paid — pending verification by our team.');
        load();
      } else {
        toast.error(json.message || 'Failed to report payment');
      }
    } catch (e) {
      toast.error('Connection error');
    } finally {
      setReporting(false);
    }
  };

  // TODO: no backend flow exists yet for this — just confirms the click
  // rather than pretending a request was actually sent anywhere.
  const handleRequestMethodChange = () => {
    toast.info('Request noted — this is not yet connected to a ticketing system. Use Support instead for now.');
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';
  const formatMoney = (n) => n != null ? `PKR ${Number(n).toLocaleString()}` : '—';

  if (loading) {
    return (
      <div className="loader-container">
        <div className="loader"></div>
        <p>Loading...</p>
      </div>
    );
  }

  const sub = data?.subscription;
  const invoices = data?.invoices || [];
  const filteredInvoices = statusFilter === 'all' ? invoices : invoices.filter(i => i.status === statusFilter);
  const currentInvoice = invoices.find(i => i.status !== 'paid');
  const cod = data?.payment_methods?.cod === '1';
  const bankTransfer = data?.payment_methods?.bank_transfer === '1';

  return (
    <div className="bp-page">
      <div className="bp-header">
        <h1>Payments</h1>
        <p>Manage your monthly subscription, payment history, and store payment methods.</p>
        <p className="bp-note">Your monthly website subscription keeps your store live, updated, and supported.</p>
      </div>

      <div className="bp-stats-grid">
        <div className="bp-stat-card">
          <p className="bp-stat-title">Current Plan</p>
          <h3 className="bp-stat-value">{sub.plan_name}</h3>
          <p className="bp-stat-sub">Up to {sub.product_limit} products</p>
        </div>
        <div className="bp-stat-card">
          <p className="bp-stat-title">Billing Status</p>
          <h3 className="bp-stat-value" style={{ textTransform: 'capitalize' }}>{sub.status.replace('_', ' ')}</h3>
          <p className="bp-stat-sub">Store subscription status</p>
        </div>
        <div className="bp-stat-card">
          <p className="bp-stat-title">Next Due Date</p>
          <h3 className="bp-stat-value">{formatDate(sub.next_due_date)}</h3>
          <p className="bp-stat-sub">Upcoming monthly payment</p>
        </div>
        <div className="bp-stat-card">
          <p className="bp-stat-title">Monthly Fee</p>
          <h3 className="bp-stat-value">{formatMoney(sub.monthly_amount)}</h3>
          <p className="bp-stat-sub">Website subscription</p>
        </div>
      </div>

      <div className="bp-card">
        <div className="bp-card-header">
          <h2>Subscription Status</h2>
          <span className={`bp-badge ${sub.status === 'active' ? 'bp-badge-active' : 'bp-badge-warn'}`}>
            {sub.status.replace('_', ' ')}
          </span>
        </div>
        <div className="bp-detail-grid">
          <div><span>Plan Name</span><strong>{sub.plan_name}</strong></div>
          <div><span>Monthly Fee</span><strong>{formatMoney(sub.monthly_amount)}</strong></div>
          <div><span>Billing Status</span><strong>{sub.status.replace('_', ' ')}</strong></div>
          <div><span>Next Due Date</span><strong>{formatDate(sub.next_due_date)}</strong></div>
        </div>
      </div>

      {currentInvoice && (
        <div className="bp-card">
          <div className="bp-card-header">
            <h2>Monthly Payment Link</h2>
            <span className={`bp-badge ${STATUS_CLASSES[currentInvoice.status]}`}>{STATUS_LABELS[currentInvoice.status]}</span>
          </div>
          <div className="bp-detail-grid">
            <div><span>Current Month</span><strong>{currentInvoice.period_label}</strong></div>
            <div><span>Amount</span><strong>{formatMoney(currentInvoice.amount)}</strong></div>
          </div>
          {sub.payment_link && (
            <div className="bp-link-box">
              <span>Payment Link</span>
              <strong>{sub.payment_link}</strong>
            </div>
          )}
          <div className="bp-btn-row">
            <button className="bp-outline-btn" onClick={handleCopyLink}>Copy Payment Link</button>
            {sub.payment_link && <a className="bp-outline-btn" href={sub.payment_link} target="_blank" rel="noopener noreferrer">Open Payment Link</a>}
            <button className="bp-dark-btn" onClick={handleMarkPaid} disabled={reporting || currentInvoice.status !== 'unpaid'}>
              {reporting ? 'Reporting...' : currentInvoice.status === 'reported' ? 'Reported — Pending Review' : 'Mark as Paid'}
            </button>
          </div>
          <p className="bp-muted">Final payment confirmation will be verified by our team.</p>
        </div>
      )}

      <div className="bp-card">
        <div className="bp-card-header">
          <div>
            <h2>Payment History</h2>
            <p className="bp-muted">Your past monthly subscription invoices.</p>
          </div>
          <div className="bp-filter-row">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All Payments</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
              <option value="reported">Reported</option>
            </select>
            <button className="bp-outline-btn" onClick={() => setStatusFilter('all')}>Reset</button>
          </div>
        </div>

        {filteredInvoices.length === 0 ? (
          <p className="bp-muted">No invoices yet.</p>
        ) : (
          <table className="bp-table">
            <thead>
              <tr><th>Invoice</th><th>Month</th><th>Amount</th><th>Status</th><th>Due Date</th><th>Paid Date</th></tr>
            </thead>
            <tbody>
              {filteredInvoices.map(inv => (
                <tr key={inv.id}>
                  <td className="bp-invoice-id">{inv.invoice_number}</td>
                  <td>{inv.period_label}</td>
                  <td>{formatMoney(inv.amount)}</td>
                  <td><span className={`bp-pill ${STATUS_CLASSES[inv.status]}`}>{STATUS_LABELS[inv.status]}</span></td>
                  <td className="bp-muted">{formatDate(inv.due_date)}</td>
                  <td className="bp-muted">{inv.paid_date ? formatDate(inv.paid_date) : 'Not paid'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bp-card">
        <h2>Store Payment Methods</h2>
        <p className="bp-muted">These are the payment options currently available to customers on your online store.</p>
        <div className="bp-methods-grid">
          <div className="bp-method-card">
            <div className="bp-method-header"><strong>Cash on Delivery</strong><span className={`bp-badge ${cod ? 'bp-badge-active' : 'bp-badge-warn'}`}>{cod ? 'Active' : 'Pending Setup'}</span></div>
            <p>Customers can place orders and pay at delivery.</p>
          </div>
          <div className="bp-method-card">
            <div className="bp-method-header"><strong>Bank Transfer</strong><span className={`bp-badge ${bankTransfer ? 'bp-badge-active' : 'bp-badge-warn'}`}>{bankTransfer ? 'Active' : 'Pending Setup'}</span></div>
            <p>Customers can transfer payment manually after placing an order.</p>
          </div>
          <div className="bp-method-card">
            <div className="bp-method-header"><strong>Online Payment</strong><span className="bp-badge bp-badge-warn">Pending Setup</span></div>
            <p>Card or online gateway integration is not active yet.</p>
          </div>
        </div>
        <button className="bp-outline-btn" onClick={handleRequestMethodChange}>Request Payment Method Change</button>
      </div>

      <div className="bp-card bp-notes-card">
        <h2>Billing Notes</h2>
        <p>Your store remains active as long as your monthly subscription is paid on time. If a payment is overdue, our team may contact you before taking any action on your store.</p>
        <p>For billing questions, please use the Support section.</p>
      </div>
    </div>
  );
};

export default Payments;